import { useRef, useState } from 'react';

import api from '../../../utils/api';
import {
  ExpoSpeechRecognitionModule,
  useSpeechRecognitionEvent,
} from 'expo-speech-recognition';

type SetValue = (name: string, value: any) => void;

interface UnitAutocompleteRef {
  selectByUuid: (uuid: string) => void;
  setQuery: (value: string) => void;
}

interface Options {
  communityId: number | string;
  setValue: SetValue;
  unitRef: React.MutableRefObject<UnitAutocompleteRef | undefined>;
}

const TEXT_FIELDS = ['guest_name', 'guest_type', 'vehicle_brand', 'vehicle_plate_number', 'comments'];
const HIGHLIGHT_MS = 2500;

/**
 * Encapsula el dictado por voz del registro de entrada: captura el audio,
 * lo manda al backend para extraer campos y los fusiona en el formulario.
 *
 * Merge incremental: solo se actualizan los campos que el dictado detecto
 * (los no mencionados llegan null y no tocan el formulario), por lo que se
 * puede dictar en tandas o corregir un campo redictandolo.
 */
export default function useVoiceEntryForm({ communityId, setValue, unitRef }: Options) {
  const [recognizing, setRecognizing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [highlighted, setHighlighted] = useState<string[]>([]);

  const transcriptRef = useRef('');
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>();

  useSpeechRecognitionEvent('start', () => setRecognizing(true));
  useSpeechRecognitionEvent('result', (event) => {
    transcriptRef.current = event.results[0]?.transcript ?? '';
  });
  useSpeechRecognitionEvent('end', () => {
    setRecognizing(false);
    const transcript = transcriptRef.current.trim();
    if (transcript) parseAndMerge(transcript);
  });
  useSpeechRecognitionEvent('error', (event) => {
    console.log('speech error:', event.error, event.message);
    setRecognizing(false);
  });

  async function toggleVoice() {
    if (recognizing) {
      ExpoSpeechRecognitionModule.stop();
      return;
    }

    const result = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
    if (!result.granted) {
      console.warn('Permisos de microfono no otorgados', result);
      return;
    }

    transcriptRef.current = '';
    ExpoSpeechRecognitionModule.start({
      lang: 'es-MX',
      interimResults: true,
      continuous: true,
      androidIntentOptions: {
        // free_form tolera pausas (web_search corta en cuanto callas)
        EXTRA_LANGUAGE_MODEL: 'free_form',
        // Tolerar ~4s de silencio antes de cerrar (best-effort: algunos equipos lo ignoran)
        EXTRA_SPEECH_INPUT_COMPLETE_SILENCE_LENGTH_MILLIS: 4000,
        EXTRA_SPEECH_INPUT_POSSIBLY_COMPLETE_SILENCE_LENGTH_MILLIS: 4000,
        EXTRA_SPEECH_INPUT_MINIMUM_LENGTH_MILLIS: 6000,
      },
      androidRecognitionServicePackage: 'com.google.android.tts',
    });
  }

  function highlightFields(fields: string[]) {
    if (fields.length === 0) return;
    setHighlighted(fields);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlighted([]), HIGHLIGHT_MS);
  }

  async function parseAndMerge(transcript: string) {
    setProcessing(true);
    try {
      console.log('Parsing voice entry:', transcript);
      const { data } = await api.post(
        `/communities/${communityId}/entry-logs/parse-voice`,
        { transcript }
      );
      console.log('Parsed data:', data);

      const filled: string[] = [];

      TEXT_FIELDS.forEach((field) => {
        if (data[field] != null) {
          setValue(field, data[field]);
          filled.push(field);
        }
      });

      // Vivienda: match unico -> autoselecciona; parcial -> precarga la busqueda
      if (data.unit_uuid) {
        unitRef.current?.selectByUuid(data.unit_uuid);
        filled.push('unit');
      } else if (data.unit_street_name) {
        const query = [data.unit_street_name, data.unit_external_number, data.unit_internal_number]
          .filter(Boolean)
          .join(' ');
        unitRef.current?.setQuery(query);
        filled.push('unit');
      }

      highlightFields(filled);
    } catch (error) {
      console.log('parse-voice error:', error);
    } finally {
      setProcessing(false);
    }
  }

  return {
    recognizing,
    processing,
    toggleVoice,
    isHighlighted: (field: string) => highlighted.includes(field),
  };
}
